import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Mesh as MeshClass } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Vector3, Quaternion, Matrix } from '@babylonjs/core/Maths/math.vector';
import { Curve3, Path3D } from '@babylonjs/core/Maths/math.path';
import type { SpatialMaterialSpec } from './spatial-material';
import { triangulate } from './mesh-earcut';
import { polygonOptions } from './mesh-polygon';

/** The materializer's scratch scene and budget hooks. */
export interface IdiomDeps {
    scratch: Scene;
    /** Count a freshly built mesh against the budgets. */
    register: (mesh: Mesh) => Mesh;
    /** Attribute-normalising merge that keeps one slot per material. */
    merge: (meshes: Mesh[], name?: string) => Mesh;
    material: (spec?: SpatialMaterialSpec) => PBRMaterial;
}

type Point = Vector3 | number[];

/** Path samples a rope strand may take: 1,000 m of lay at twelve a turn is about 100k triangles at 8 sides. */
const MAX_ROPE_SAMPLES = 6_000;

const AXES: Record<string, readonly number[]> = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] };

/** A point or direction from model code: `[x, y, z]`, a Vector3 or `{x, y, z}`, or an axis letter (`"x"`, as prism's axis). */
export function toV3(p: Point | string | undefined, fallback = Vector3.Zero()): Vector3 {
    if (!p) return fallback;
    if (typeof p === 'string' && Object.hasOwn(AXES, p)) return Vector3.FromArray(AXES[p]);
    if (Array.isArray(p)) return new Vector3(p[0] ?? 0, p[1] ?? 0, p[2] ?? 0);
    const v = p as { x?: unknown; y?: unknown; z?: unknown };
    if (typeof v === 'object' && [v.x, v.y, v.z].every(n => typeof n === 'number')) return new Vector3(v.x as number, v.y as number, v.z as number);
    throw new Error(`expected [x, y, z] or "x" | "y" | "z", got ${JSON.stringify(p)}`);
}

/**
 * A deterministic RNG, because "individually varied" must still be
 * reproducible: the same seed has to give the same wall on a re-run, or the
 * model cannot iterate on a render and the tests cannot pin a triangle count.
 * mulberry32 — 4 lines, and `Math.random` stays available for anyone who
 * wants noise instead.
 */
function rng(seed: number): () => number {
    let a = (seed >>> 0) || 1;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function faceNormal(face: number[], verts: number[][]): number[] {
    const n = [0, 0, 0];
    for (let i = 0; i < face.length; i++) {
        const p = verts[face[i]], q = verts[face[(i + 1) % face.length]];
        n[0] += (p[1] - q[1]) * (p[2] + q[2]);
        n[1] += (p[2] - q[2]) * (p[0] + q[0]);
        n[2] += (p[0] - q[0]) * (p[1] + q[1]);
    }
    return n;
}

/** Order a convex planar face's vertices by angle about their own centroid. */
function orderFace(face: number[], verts: number[][]): number[] {
    const c = [0, 1, 2].map(k => face.reduce((s, i) => s + verts[i][k], 0) / face.length);
    const n = c.map(v => v / (Math.hypot(...c) || 1));
    const t0 = [0, 1, 2].map(k => verts[face[0]][k] - c[k]);
    const tl = Math.hypot(...t0) || 1;
    const t = t0.map(v => v / tl);
    const b = [n[1] * t[2] - n[2] * t[1], n[2] * t[0] - n[0] * t[2], n[0] * t[1] - n[1] * t[0]];
    const angle = (i: number): number => {
        const d = [0, 1, 2].map(k => verts[i][k] - c[k]);
        return Math.atan2(d[0] * b[0] + d[1] * b[1] + d[2] * b[2], d[0] * t[0] + d[1] * t[1] + d[2] * t[2]);
    };
    const ordered = [...face].sort((i, j) => angle(i) - angle(j));
    const fn = faceNormal(ordered, verts);
    return fn[0] * c[0] + fn[1] * c[1] + fn[2] * c[2] < 0 ? ordered.reverse() : ordered;
}

/** 24 vertices and 26 faces of a chamfered box, before any taper. */
function chamferTopology(w: number, h: number, d: number, bevel: number): { verts: number[][]; faces: number[][] } {
    const half = [w / 2, h / 2, d / 2];
    const r = Math.max(0, Math.min(bevel, half[0] * 0.49, half[1] * 0.49, half[2] * 0.49));
    const signs: number[][] = [];
    for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) signs.push([x, y, z]);
    const verts: number[][] = [];
    for (const s of signs) {
        for (let a = 0; a < 3; a++) verts.push([0, 1, 2].map(k => s[k] * (k === a ? half[k] : half[k] - r)));
    }
    const at = (corner: number, axis: number): number => corner * 3 + axis;
    const faces: number[][] = [];
    for (let a = 0; a < 3; a++) {
        for (const sa of [-1, 1]) {
            faces.push(signs.flatMap((s, c) => (s[a] === sa ? [at(c, a)] : [])));
            // bevel 0 collapses the three vertices of a corner onto each
            // other; emitting the edge and corner faces anyway would hand
            // ComputeNormals 20 zero-area triangles and a NaN normal each.
            if (r === 0) continue;
            for (let bx = a + 1; bx < 3; bx++) {
                for (const sb of [-1, 1]) {
                    faces.push(signs.flatMap((s, c) => (s[a] === sa && s[bx] === sb ? [at(c, a), at(c, bx)] : [])));
                }
            }
        }
    }
    if (r > 0) for (let c = 0; c < 8; c++) faces.push([at(c, 0), at(c, 1), at(c, 2)]);
    return { verts, faces: faces.map(f => orderFace(f, verts)) };
}

function chamferBoxData(w: number, h: number, d: number, bevel: number, taperX: number, taperZ: number): VertexData {
    const { verts, faces } = chamferTopology(w, h, d, bevel);
    const positions: number[] = [];
    const indices: number[] = [];
    for (const face of faces) {
        const base = positions.length / 3;
        for (const i of face) {
            const [x, y, z] = verts[i];
            const t = h === 0 ? 0 : y / h + 0.5;
            const sx = taperX === 1 ? 1 : 1 + (taperX - 1) * t;
            const sz = taperZ === 1 ? 1 : 1 + (taperZ - 1) * t;
            positions.push(x * sx, y, z * sz);
        }
        for (let k = 1; k < face.length - 1; k++) indices.push(base, base + k + 1, base + k);
    }
    const data = new VertexData();
    data.positions = positions;
    data.indices = indices;
    // After the flip, so the normals follow the winding rather than fighting it.
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    data.normals = normals;
    data.uvs = new Array((positions.length / 3) * 2).fill(0);
    return data;
}

// ── The seven idioms ────────────────────────────────────

type BoxOpts = {
    w?: number; h?: number; d?: number; size?: number;
    bevel?: number; taper?: number; at?: Point; rotation?: Point; material?: PBRMaterial;
    /** Between two points instead of centred on `at`: h becomes |to-from|. */
    from?: Point; to?: Point;
    /** Absolute section at the far end. `wTop` alone narrows both axes alike. */
    wTop?: number; dTop?: number;
    /** Rotation about Y only, radians — how a canal house sits off the grid. */
    yaw?: number;
};
type BarOpts = { material?: PBRMaterial; bevel?: number };
type RodOpts = {
    sides?: number; smooth?: number; cap?: boolean; material?: PBRMaterial; r2?: number;
    /** Radius at each control point, overriding radius and r2: a horn, a tapering branch. */
    radii?: number[];
    /** Twisted strands laid around the path, rope-style, `twist` turns per metre (default a rope's lay, 1 / 7r). */
    strands?: number; twist?: number;
};
type LatheOpts = {
    segments?: number; at?: Point; axis?: Point | 'x' | 'y' | 'z';
    materials?: PBRMaterial[]; bands?: number[]; material?: PBRMaterial;
};
type PrismOpts = { axis?: 'x' | 'y' | 'z'; at?: Point; holes?: number[][][]; material?: PBRMaterial };
type SlabOpts = {
    w?: number; h?: number; d?: number; planks?: number; gap?: number; bevel?: number;
    along?: 'x' | 'z'; at?: Point; materials?: PBRMaterial[]; material?: PBRMaterial;
};
type WallOpts = {
    w?: number; h?: number; d?: number; rows?: number; course?: number; jitter?: number;
    gap?: number; bevel?: number; seed?: number; at?: Point; backing?: boolean;
    materials?: PBRMaterial[]; material?: PBRMaterial; mortar?: PBRMaterial;
};
type ShingleOpts = {
    w?: number; run?: number; rise?: number; rows?: number; tile?: number; overlap?: number;
    thickness?: number; gap?: number; seed?: number; at?: Point;
    materials?: PBRMaterial[]; material?: PBRMaterial;
};
type SpokeOpts = {
    count?: number; hubRadius?: number; rimRadius?: number; hubWidth?: number; radius?: number;
    lacing?: number; sides?: number; at?: Point; axis?: Point; material?: PBRMaterial;
};

/**
 * Signed area of a lathe profile closed back along the axis. Negative means
 * the profile runs top to bottom, which CreateLathe revolves inside out.
 */
function profileArea(pts: Vector3[]): number {
    const loop = [...pts, new Vector3(0, pts[pts.length - 1].y, 0), new Vector3(0, pts[0].y, 0)];
    let area = 0;
    for (let i = 0; i < loop.length; i++) {
        const a = loop[i], b = loop[(i + 1) % loop.length];
        area += a.x * b.y - b.x * a.y;
    }
    return area;
}

/**
 * ExtrudePolygon's (u, -depth..0, v) onto the plane across `axis`, depth
 * centred on it. Each is a proper rotation, so the winding stays outward.
 */
function prismFrame(axis: 'x' | 'y' | 'z', depth: number): Matrix {
    const h = depth / 2;
    if (axis === 'x') return Matrix.FromValues(0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0, h, 0, 0, 1);
    if (axis === 'y') return Matrix.FromValues(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, h, 0, 1);
    return Matrix.FromValues(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, -h, 1);
}

/** Point the mesh's local +Y down `axis`. */
function orientTo(mesh: Mesh, axis: Vector3): void {
    const dir = axis.clone().normalize();
    const q = new Quaternion();
    Quaternion.FromUnitVectorsToRef(Vector3.Up(), dir, q);
    mesh.rotationQuaternion = q;
}

export function buildIdioms(deps: IdiomDeps): Record<string, unknown> {
    const { scratch, register, merge } = deps;
    const pick = (opts: { materials?: PBRMaterial[]; material?: PBRMaterial }): PBRMaterial[] =>
        (opts.materials?.length ? opts.materials : [opts.material ?? deps.material()]);


    const buildBox = (name: string, opts: BoxOpts = {}): Mesh => {
        const span = opts.from && opts.to ? toV3(opts.to).subtract(toV3(opts.from)) : null;
        const w = opts.w ?? opts.size ?? 1;
        const h = span ? span.length() : (opts.h ?? opts.size ?? w);
        const d = opts.d ?? opts.size ?? w;
        if (span && !(h > 0)) throw new Error('box(name, {from, to}) needs two different points');
        const taperX = opts.wTop !== undefined && w > 0 ? opts.wTop / w : (opts.taper ?? 1);
        const taperZ = opts.dTop !== undefined && d > 0 ? opts.dTop / d : taperX;
        const bevel = opts.bevel ?? Math.min(0.006, Math.min(w, h, d) * 0.06);
        const mesh = new MeshClass(name, scratch);
        chamferBoxData(w, h, d, bevel, taperX, taperZ).applyToMesh(mesh);
        if (span) {
            orientTo(mesh, span);
            mesh.position = toV3(opts.from).add(span.scale(0.5));
        } else {
            mesh.position = toV3(opts.at);
            if (opts.rotation) mesh.rotation = toV3(opts.rotation);
            else if (opts.yaw) mesh.rotation = new Vector3(0, opts.yaw, 0);
        }
        mesh.material = opts.material ?? deps.material();
        return register(mesh);
    };


    const box = (
        name: string,
        at?: BoxOpts | Point,
        size?: number | Point,
        material?: PBRMaterial,
        opts: BoxOpts = {},
    ): Mesh => {
        const positional = at !== undefined && (Array.isArray(at) || at instanceof Vector3);
        if (!positional) return buildBox(name, at ?? {});
        const dims: BoxOpts = size === undefined
            ? {}
            : typeof size === 'number'
                ? { size }
                : { w: toV3(size).x, h: toV3(size).y, d: toV3(size).z };
        return buildBox(name, { ...opts, ...dims, at, material: material ?? opts.material });
    };


    const bar = (name: string, a: Point, b: Point, w = 0.05, d = w, opts: BarOpts = {}): Mesh => {
        const from = toV3(a), to = toV3(b);
        const along = to.subtract(from);
        const length = along.length();
        if (!(length > 0)) throw new Error('bar(name, a, b, w, d) needs two different points');
        const mesh = new MeshClass(name, scratch);
        const bevel = opts.bevel ?? Math.min(0.006, Math.min(w, d) * 0.06);
        chamferBoxData(w, length, d, bevel, 1, 1).applyToMesh(mesh);
        orientTo(mesh, along);
        mesh.position = from.add(along.scale(0.5));
        mesh.material = opts.material ?? deps.material();
        return register(mesh);
    };


    const rod = (name: string, points: Point[], radius = 0.01, opts: RodOpts = {}): Mesh => {
        const pts = (points ?? []).map(p => toV3(p));
        if (pts.length < 2) throw new Error('rod(name, [a, b, …], radius) needs at least two points');
        if (opts.radii && opts.radii.length !== pts.length) throw new Error('rod(name, points, r, {radii}) needs one radius per point');
        const smooth = opts.smooth ?? (pts.length > 16 ? 0 : 6);
        const splined = pts.length > 2 && smooth > 0;
        const path = splined ? Curve3.CreateCatmullRomSpline(pts, smooth).getPoints() : pts;
        // Each path point's position in control-point units, so `radii` land
        // on their points after the spline adds samples between them.
        const along = path.map((_, i) => (splined ? i / smooth : i));
        if (opts.radii?.some(r => !Number.isFinite(r))) throw new Error('rod(name, points, r, {radii}) needs finite radii');
        const strands = Math.round(opts.strands ?? 1);
        if (!Number.isFinite(strands)) throw new Error(`rod(name, points, r, {strands}) needs a finite count, got ${JSON.stringify(opts.strands)}`);
        const twist = opts.twist ?? 1 / (7 * radius);
        if (strands > 1 && !(radius > 0 && Number.isFinite(twist))) {
            throw new Error('rod(name, points, r, {strands}) needs a positive radius and a finite twist');
        }
        if (strands > 1 && twist !== 0) {
            let length = 0;
            for (let i = 1; i < path.length; i++) length += Vector3.Distance(path[i - 1], path[i]);
            const samples = Math.ceil(length * Math.abs(twist) * 12);
            if (samples > MAX_ROPE_SAMPLES) {
                throw new Error(`rod: ${Math.abs(twist).toFixed(1)} turns/m over ${length.toFixed(2)} m is ${samples.toLocaleString()} samples a strand, over ${MAX_ROPE_SAMPLES.toLocaleString()}. Lower twist, or build the rope in shorter pieces.`);
            }
            // About twelve samples per turn, or the strands kink. Negative twist is the opposite lay.
            const step = 1 / (Math.abs(twist) * 12);
            for (let i = path.length - 1; i > 0; i--) {
                const n = Math.ceil(Vector3.Distance(path[i - 1], path[i]) / step);
                for (let k = n - 1; k > 0; k--) {
                    path.splice(i, 0, Vector3.Lerp(path[i - 1], path[i], k / n));
                    along.splice(i, 0, along[i - 1] + (along[i] - along[i - 1]) * (k / n));
                }
            }
        }
        // Distance along the FINAL path, spline included, or a smoothed rod tapers early.
        const distance = [0];
        for (let i = 1; i < path.length; i++) distance.push(distance[i - 1] + Vector3.Distance(path[i - 1], path[i]));
        const total = distance[distance.length - 1];
        const radiusAt = (i: number): number => {
            if (opts.radii) {
                const u = Math.min(along[i], opts.radii.length - 1), k = Math.floor(u);
                return opts.radii[k] + ((opts.radii[k + 1] ?? opts.radii[k]) - opts.radii[k]) * (u - k);
            }
            return opts.r2 !== undefined && total > 0 ? radius + (opts.r2 - radius) * (distance[i] / total) : radius;
        };
        const tube = (tubeName: string, tubePath: Vector3[], scale: number): Mesh => {
            const mesh = MeshBuilder.CreateTube(tubeName, {
                path: tubePath, radius, tessellation: opts.sides ?? 8,
                cap: opts.cap === false ? MeshClass.NO_CAP : MeshClass.CAP_ALL,
                radiusFunction: (i: number) => radiusAt(i) * scale,
            }, scratch);
            mesh.material = opts.material ?? deps.material();
            return register(mesh);
        };
        if (strands < 2) return tube(name, path, 1);
        // Strands touching their neighbours and filling the rope's radius.
        const s = Math.sin(Math.PI / strands), lay = 1 / (1 + s);
        const frame = new Path3D(path);
        const normals = frame.getNormals(), binormals = frame.getBinormals();
        const parts: Mesh[] = [];
        for (let k = 0; k < strands; k++) {
            const strandPath = path.map((p, i) => {
                const a = (2 * Math.PI * k) / strands + 2 * Math.PI * twist * distance[i];
                return p.add(normals[i].scale(Math.cos(a)).add(binormals[i].scale(Math.sin(a))).scale(radiusAt(i) * lay));
            });
            parts.push(tube(`${name}_strand${k + 1}`, strandPath, s * lay));
        }
        return merge(parts, name);
    };

    /**
     * Points along a helix from `a` to `b` — feed it to `rod()` for a real
     * spring. `turns x steps` segments; the default 8 x 16 through a 6-sided
     * rod is ~1,550 triangles.
     */
    const helix = (a: Point, b: Point, radius = 0.01, turns = 8, steps = 16): Vector3[] => {
        const from = toV3(a), to = toV3(b);
        const along = to.subtract(from);
        const seed = Math.abs(along.y) > Math.abs(along.x) ? Vector3.Right() : Vector3.Up();
        const u = Vector3.Cross(along, seed).normalize();
        const v = Vector3.Cross(along, u).normalize();
        const out: Vector3[] = [];
        const n = Math.max(8, Math.round(turns * steps));
        for (let i = 0; i <= n; i++) {
            const t = i / n, a2 = t * turns * 2 * Math.PI;
            out.push(from.add(along.scale(t))
                .add(u.scale(Math.cos(a2) * radius)).add(v.scale(Math.sin(a2) * radius)));
        }
        return out;
    };

    /**
     * A turned profile — `[[radius, y], …]` revolved about the axis — with one
     * material BAND per segment, which is how a lamp shade gets a brass collar
     * and an ivory interior on one mesh. 2 x segments x (profile points - 1)
     * triangles before removing collapsed pole/profile triangles. A 32-segment,
     * 8-point profile with two axial endpoints has 384.
     */
    const lathe = (name: string, profile: (number[] | Vector3)[], opts: LatheOpts = {}): Mesh => {
        const pts = profile.map(p => (Array.isArray(p) ? new Vector3(p[0], p[1], 0) : new Vector3(p.x, p.y, 0)));
        if (pts.length < 2) throw new Error('lathe(name, [[radius, y], …]) needs at least two profile points');
        const mats = pick(opts);
        const bands = opts.bands ?? [];
        const flip = profileArea(pts) < 0;
        const parts: Mesh[] = [];
        let start = 0;
        for (let i = 1; i < pts.length; i++) {
            const here = bands[i - 1] ?? 0;
            const next = i < pts.length - 1 ? (bands[i] ?? 0) : NaN;
            if (here === next) continue;
            const part = MeshBuilder.CreateLathe(`${name}_band${parts.length + 1}`, {
                shape: flip ? pts.slice(start, i + 1).reverse() : pts.slice(start, i + 1),
                tessellation: opts.segments ?? 32,
                // CreateLathe already includes both 0 and 2π. Closing its
                // ribbon again adds a zero-area strip at the same seam.
                closed: false,
            }, scratch);
            // A ribbon quad at the axis contains one real cap triangle and
            // one triangle with two identical positions. Repeated profile
            // points likewise create collapsed strips. Drop only exact
            // coincidences: do not weld small bores or change normals/UVs.
            const positions = part.getVerticesData(VertexBuffer.PositionKind);
            const indices = part.getIndices();
            if (positions && indices) {
                const same = (a: number, b: number) => [0, 1, 2].every(k => positions[a * 3 + k] === positions[b * 3 + k]);
                const kept: number[] = [];
                for (let j = 0; j < indices.length; j += 3) {
                    const a = indices[j], b = indices[j + 1], c = indices[j + 2];
                    if (!same(a, b) && !same(b, c) && !same(c, a)) kept.push(a, b, c);
                }
                if (kept.length !== indices.length) part.setIndices(kept);
            }
            part.material = mats[here] ?? mats[0];
            parts.push(register(part));
            start = i;
        }
        const mesh = merge(parts, name);
        if (opts.axis) orientTo(mesh, toV3(opts.axis));
        mesh.position = toV3(opts.at);
        return mesh;
    };

    /**
     * A 2D profile extruded along `axis`, depth centred on it: a side profile
     * (axis z, [x, y]), a front profile (axis x, [z, y]) or a plan (axis y,
     * [x, z]). Any loop order; holes allowed.
     */
    const prism = (name: string, profile: number[][], depth: number, opts: PrismOpts = {}): Mesh => {
        if (!(depth > 0)) throw new Error('prism(name, [[u, v], …], depth) needs a positive depth');
        const loop = (ring: number[][]): Vector3[] => ring.map(p => new Vector3(p[0], 0, p[1]));
        const mesh = MeshBuilder.ExtrudePolygon(name, polygonOptions({
            shape: loop(profile ?? []), holes: (opts.holes ?? []).map(loop), depth,
        }) as Parameters<typeof MeshBuilder.ExtrudePolygon>[1], scratch, triangulate);
        mesh.bakeTransformIntoVertices(prismFrame(opts.axis ?? 'z', depth));
        mesh.position = toV3(opts.at);
        mesh.material = opts.material ?? deps.material();
        return register(mesh);
    };

    /** Boards with gaps, alternating through the palette. planks x 44 triangles. */
    const plankedSlab = (name: string, opts: SlabOpts = {}): Mesh => {
        const w = opts.w ?? 0.44, h = opts.h ?? 0.032, d = opts.d ?? 0.44;
        const count = Math.max(1, Math.round(opts.planks ?? 4));
        const gap = opts.gap ?? 0.003, along = opts.along ?? 'x';
        const mats = pick(opts);
        const span = along === 'x' ? w : d;
        const pitch = span / count;
        const parts: Mesh[] = [];
        for (let i = 0; i < count; i++) {
            const off = -span / 2 + pitch * (i + 0.5);
            parts.push(buildBox(`${name}_plank${i + 1}`, {
                w: along === 'x' ? pitch - gap : w, h, d: along === 'x' ? d : pitch - gap,
                bevel: opts.bevel ?? Math.min(0.0025, h * 0.25),
                at: along === 'x' ? [off, 0, 0] : [0, 0, off],
                material: mats[i % mats.length],
            }));
        }
        const mesh = merge(parts, name);
        mesh.position = toV3(opts.at);
        return mesh;
    };

    /**
     * Coursed masonry: every block its own size, depth and stone, joints broken
     * row to row, on a recessed mortar backing. Seeded, so a re-run is the same
     * wall. blocks x 44 triangles — a 6 x 2.5 m wall at 10 rows is ~120 blocks,
     * 5,280 triangles.
     */
    const coursedWall = (name: string, opts: WallOpts = {}): Mesh => {
        const w = opts.w ?? 4, h = opts.h ?? 2.5, d = opts.d ?? 0.3;
        const rows = Math.max(1, Math.round(opts.rows ?? 10));
        const jitter = opts.jitter ?? 0.3, gap = opts.gap ?? 0.012;
        const course = opts.course ?? Math.max(0.12, w / 8);
        const mats = pick(opts);
        const rand = rng(opts.seed ?? 7);
        const rowH = h / rows;
        const parts: Mesh[] = [];
        if (opts.backing !== false) {
            parts.push(buildBox(`${name}_mortar`, {
                w, h, d: d * 0.62, bevel: 0, at: [0, 0, -d * 0.19],
                material: opts.mortar ?? mats[0],
            }));
        }
        for (let r = 0; r < rows; r++) {
            const y = -h / 2 + rowH * (r + 0.5);
            let x = -w / 2, first = true;
            while (x < w / 2 - gap) {
                const nominal = first && r % 2 === 1 ? course * 0.55 : course * (1 + (rand() * 2 - 1) * jitter);
                const bw = Math.min(nominal, w / 2 - x);
                const dd = d * (1 + (rand() * 2 - 1) * jitter * 0.3);
                first = false;
                if (bw > gap * 1.5) {
                    parts.push(buildBox(`${name}_block`, {
                        w: bw - gap, h: rowH - gap, d: dd,
                        bevel: opts.bevel ?? Math.min(0.02, rowH * 0.09),
                        at: [x + bw / 2, y, (dd - d) / 2],
                        material: mats[Math.floor(rand() * mats.length) % mats.length],
                    }));
                }
                x += bw;
            }
        }
        const mesh = merge(parts, name);
        mesh.position = toV3(opts.at);
        return mesh;
    };

    /**
     * Overlapping tile courses on a pitched plane — eave at -run/2, ridge at
     * +run/2, `rise` metres higher — with every other row offset half a tile
     * and each tile its own slate. tiles x 44 triangles; 10 rows of a 6 m roof
     * at 0.4 m tiles is ~150 tiles, 6,600 triangles.
     */
    const shingles = (name: string, opts: ShingleOpts = {}): Mesh => {
        const w = opts.w ?? 6, run = opts.run ?? 2.4, rise = opts.rise ?? 1.2;
        const rows = Math.max(1, Math.round(opts.rows ?? 10));
        const tile = opts.tile ?? 0.4, thickness = opts.thickness ?? 0.02;
        const overlap = opts.overlap ?? 0.45, gap = opts.gap ?? 0.006;
        const mats = pick(opts);
        const rand = rng(opts.seed ?? 11);
        const rowDepth = run / rows, slope = rise / run, pitch = Math.atan(slope);
        const parts: Mesh[] = [];
        for (let r = 0; r < rows; r++) {
            const z = -run / 2 + rowDepth * (r + 0.5);
            const y = (z + run / 2) * slope + r * thickness * 0.1;
            for (let x = -w / 2 - (r % 2 ? tile / 2 : 0); x < w / 2 - gap; x += tile) {
                const left = Math.max(x, -w / 2), right = Math.min(x + tile, w / 2);
                if (right - left < gap * 4) continue;
                parts.push(buildBox(`${name}_tile`, {
                    w: right - left - gap, h: thickness, d: rowDepth * (1 + overlap),
                    bevel: Math.min(0.004, thickness * 0.3),
                    at: [(left + right) / 2, y, z], rotation: [-pitch, 0, 0],
                    material: mats[Math.floor(rand() * mats.length) % mats.length],
                }));
            }
        }
        const mesh = merge(parts, name);
        mesh.position = toV3(opts.at);
        return mesh;
    };

    /**
     * Laced wheel spokes: alternating hub flanges, each spoke offset
     * tangentially by `lacing` radians so the pattern crosses the way a real
     * wheel does. count x 60 triangles at 6 sides — 32 spokes is 1,920.
     */
    const spokes = (name: string, opts: SpokeOpts = {}): Mesh => {
        const count = Math.max(2, Math.round(opts.count ?? 32));
        const hubR = opts.hubRadius ?? 0.03, rimR = opts.rimRadius ?? 0.3;
        const hubWidth = opts.hubWidth ?? 0.07, radius = opts.radius ?? 0.0015;
        const lacing = opts.lacing ?? 0.22;
        const axis = toV3(opts.axis ?? [1, 0, 0]).normalize();
        const at = toV3(opts.at);
        const mat = opts.material ?? deps.material();
        const e1 = Vector3.Cross(axis, Math.abs(axis.y) > 0.9 ? Vector3.Right() : Vector3.Up()).normalize();
        const e2 = Vector3.Cross(axis, e1).normalize();
        const parts: Mesh[] = [];
        for (let i = 0; i < count; i++) {
            const a = (2 * Math.PI * i) / count, side = i % 2 ? 1 : -1;
            const ha = a + side * lacing;
            const hub = at.add(axis.scale((side * hubWidth) / 2))
                .add(e1.scale(Math.cos(ha) * hubR)).add(e2.scale(Math.sin(ha) * hubR));
            const rim = at.add(axis.scale(side * 0.004))
                .add(e1.scale(Math.cos(a) * rimR)).add(e2.scale(Math.sin(a) * rimR));
            parts.push(rod(`${name}_spoke${i + 1}`, [hub, rim], radius, { sides: opts.sides ?? 6, material: mat }));
        }
        return merge(parts, name);
    };

    return { box, bevelBox: box, bar, rod, helix, lathe, prism, plankedSlab, coursedWall, shingles, spokes };
}
